import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { alpha, styled, keyframes } from "@mui/material/styles";
import { motion, useInView } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import ImageSearchOutlinedIcon from "@mui/icons-material/ImageSearchOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const MotionBox = motion(Box);

const BRAND = {
    turquoise: "#00B4A6",
    turquoiseDark: "#008B7A",
    turquoiseSoft: "#E0F7F5",
    purple: "#6366f1",
    purpleSoft: "#EAE8FF",
    gold: "#D4AF37",
    ink: "#1A1A1A",
    text: "#616161",
    border: "#DCEFED",
    bg: "#FFFFFF",
    subtle: "#FAFAFA",
};

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const DreamBackground = styled(Box)({
    position: "absolute",
    inset: 0,
    background: `linear-gradient(-45deg, ${BRAND.bg}, ${BRAND.turquoiseSoft}, ${BRAND.purpleSoft}, ${BRAND.bg})`,
    backgroundSize: "400% 400%",
    animation: `${gradientMove} 18s ease infinite`,
    zIndex: -3,
});

const GlowLayer = styled(Box)({
    position: "absolute",
    inset: 0,
    background: `
    radial-gradient(circle at 18% 20%, rgba(0,180,166,0.12), transparent 28%),
    radial-gradient(circle at 82% 24%, rgba(99,102,241,0.10), transparent 28%),
    radial-gradient(circle at 50% 85%, rgba(212,175,55,0.08), transparent 24%)
  `,
    zIndex: -2,
});

const GlassCard = styled(Card)({
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(18px)",
    border: "1px solid rgba(255,255,255,0.55)",
    borderRadius: "24px",
    boxShadow: "0 18px 45px rgba(0,0,0,0.08)",
    transition: "all 0.35s ease",
    height: "100%",
    overflow: "hidden",
    "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 24px 56px rgba(0,0,0,0.12)",
    },
});

const FloatingCard = styled(Box)({
    animation: `${float} 4.2s ease-in-out infinite`,
});

const HeroButton = styled(Button)({
    fontSize: "1rem",
    fontWeight: 700,
    padding: "15px 30px",
    borderRadius: "999px",
    textTransform: "none",
    background: `linear-gradient(135deg, ${BRAND.turquoise} 0%, ${BRAND.purple} 100%)`,
    color: "#FFFFFF",
    boxShadow: "0 10px 28px rgba(0,180,166,0.22)",
    "&:hover": {
        background: `linear-gradient(135deg, ${BRAND.turquoiseDark} 0%, ${BRAND.purple} 100%)`,
        boxShadow: "0 14px 34px rgba(0,180,166,0.30)",
    },
    "&.Mui-disabled": {
        background: "linear-gradient(135deg, #7ecfc8 0%, #9fa5ff 100%)",
        color: "rgba(255,255,255,0.85)",
    },
});

const SecondaryButton = styled(Button)({
    fontSize: "1rem",
    fontWeight: 700,
    padding: "14px 28px",
    borderRadius: "999px",
    textTransform: "none",
    color: BRAND.turquoise,
    border: `2px solid ${BRAND.turquoise}`,
    backgroundColor: "rgba(0,180,166,0.04)",
    "&:hover": {
        backgroundColor: "rgba(0,180,166,0.10)",
        borderColor: BRAND.turquoiseDark,
    },
});

const UploadDropzone = styled(Box)({
    border: `2px dashed ${BRAND.turquoise}`,
    borderRadius: "22px",
    padding: "36px 24px",
    textAlign: "center",
    cursor: "pointer",
    background: `linear-gradient(180deg, rgba(224,247,245,0.82) 0%, rgba(255,255,255,0.88) 100%)`,
    transition: "all 0.3s ease",
    "&:hover": {
        borderColor: BRAND.turquoiseDark,
        transform: "translateY(-2px)",
        boxShadow: "0 12px 28px rgba(0,180,166,0.12)",
    },
});

const buyerTestimonials = [
    {
        name: "Sophie R.",
        role: "Home decor buyer",
        quote: "I uploaded a photo of my living room and the match felt surprisingly accurate. The painting picked up the colours of the space beautifully and made the whole room feel more elegant and complete.",
    },
    {
        name: "Daniel M.",
        role: "Interior art customer",
        quote: "What I liked most was how calm and balanced the recommendation felt. The artwork suited the atmosphere of the room so well that it looked as if it had always belonged there.",
    },
    {
        name: "Leila T.",
        role: "Gift buyer",
        quote: "I used it to choose a painting as a gift and it made the decision much easier. The match felt thoughtful, stylish, and personal, and the final result brought real warmth to the room.",
    },
];

const featuredFallback = [
    {
        title: "The Kiss",
        artist: "Gustav Klimt",
        image: "/p.TheKissbyGustavKlimt.png",
    },
    {
        title: "Almond Blossom",
        artist: "Vincent van Gogh",
        image: "/p.AlmondBlossombyVincentVanGogh.jpg",
    },
    {
        title: "Water Lilies and Japanese Bridge",
        artist: "Claude Monet",
        image: "/p.WaterLiliesandJapaneseBridgebyClaudeMonet.jpg",
    },
    {
        title: "Tableau I",
        artist: "Piet Mondrian",
        image: "/p.TableauIbyPietMondrian.jpg",
    },
];

function normaliseEmbedding(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(Number).filter((x) => Number.isFinite(x));
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed.map(Number).filter((x) => Number.isFinite(x)) : [];
        } catch {
            return [];
        }
    }
    return [];
}

function cosineSimilarity(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length === 0 || b.length === 0 || a.length !== b.length) {
        return 0;
    }

    let dot = 0;
    let normA = 0;
    let normB = 0;

    for (let i = 0; i < a.length; i += 1) {
        dot += a[i] * b[i];
        normA += a[i] * a[i];
        normB += b[i] * b[i];
    }

    if (normA === 0 || normB === 0) return 0;
    return dot / (Math.sqrt(normA) * Math.sqrt(normB));
}

function extractImageUrl(item) {
    if (!item) return "";
    if (typeof item.image === "string") return item.image;
    if (typeof item.image_url === "string") return item.image_url;
    if (typeof item.imageUrl === "string") return item.imageUrl;
    if (typeof item.image_full_url === "string") return item.image_full_url;
    return "";
}

function MatchCard({ item, index }) {
    const scorePct = Math.max(1, Math.round((item.score || 0) * 100));

    return (
        <Grid item xs={12} sm={6} lg={4}>
            <MotionBox
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.16 }}
                transition={{ duration: 0.34, delay: index * 0.04 }}
            >
                <GlassCard>
                    {item.image ? (
                        <CardMedia
                            component="img"
                            image={item.image}
                            alt={item.title || "Matched painting"}
                            sx={{ height: 300, objectFit: "cover" }}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: 300,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: BRAND.subtle,
                            }}
                        >
                            <PaletteOutlinedIcon sx={{ fontSize: 56, color: BRAND.turquoiseDark }} />
                        </Box>
                    )}

                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1.4 }}>
                            <Chip
                                label="AI match"
                                sx={{
                                    backgroundColor: BRAND.turquoiseSoft,
                                    color: BRAND.turquoiseDark,
                                    fontWeight: 800,
                                }}
                            />
                            <Chip
                                icon={<InsightsOutlinedIcon sx={{ fontSize: 18 }} />}
                                label={`${scorePct}% fit`}
                                sx={{
                                    backgroundColor: alpha(BRAND.purple, 0.10),
                                    color: BRAND.purple,
                                    fontWeight: 800,
                                }}
                            />
                        </Stack>

                        <Typography
                            sx={{
                                color: BRAND.ink,
                                fontWeight: 800,
                                fontSize: "1.12rem",
                                lineHeight: 1.35,
                                mb: 0.6,
                            }}
                        >
                            {item.title || "Untitled painting"}
                        </Typography>

                        <Typography sx={{ color: BRAND.text, mb: 1.8 }}>
                            {item.artist || "Unknown artist"}
                        </Typography>

                        <Typography sx={{ color: BRAND.text, lineHeight: 1.75 }}>
                            A visual match chosen by colour rhythm, atmosphere, and image similarity.
                        </Typography>
                    </CardContent>
                </GlassCard>
            </MotionBox>
        </Grid>
    );
}

export default function RoomMatcherPage() {
    const [paintings, setPaintings] = useState([]);
    const [loadingPaintings, setLoadingPaintings] = useState(true);

    const [image, setImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [matches, setMatches] = useState([]);

    const heroRef = useRef(null);
    const resultsRef = useRef(null);
    const isInView = useInView(heroRef, { once: true });

    useEffect(() => {
        document.title =
            "AI Room Matcher | Room Decor Matching, Painting Discovery & Computer Vision | Art For My Room";

        const setMeta = (attrName, attrValue, content, type = "name") => {
            let el = document.querySelector(`meta[${type}="${attrValue}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(type, attrValue);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        const canonicalUrl = `${window.location.origin}/room-matcher`;

        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", canonicalUrl);

        setMeta(
            "description",
            "AI Room Matcher by Art For My Room helps users upload a room photo and discover paintings that fit the room decor, colour palette, mood, interior styling and visual atmosphere."
        );
        setMeta(
            "keywords",
            "AI room matcher, room decor matching, painting matcher, room to painting matcher, interior design AI, art for room decor, AI image matching, computer vision room analysis, visual search UK, image recognition systems, room style matching, painting recommendation engine"
        );
        setMeta(
            "og:title",
            "AI Room Matcher | Room Decor Matching & Painting Discovery",
            "property"
        );
        setMeta(
            "og:description",
            "Upload a room image and discover paintings that match the room decor, colour balance, atmosphere and style using AI.",
            "property"
        );
        setMeta("og:type", "website", "property");
        setMeta("og:url", canonicalUrl, "property");

        const oldSchema = document.getElementById("room-matcher-jsonld");
        if (oldSchema) oldSchema.remove();

        const schema = document.createElement("script");
        schema.id = "room-matcher-jsonld";
        schema.type = "application/ld+json";
        schema.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "WebPage",
                    name: "AI Room Matcher",
                    url: canonicalUrl,
                    description:
                        "AI room decor matching page for discovering paintings that fit a room image.",
                },
                {
                    "@type": "Service",
                    name: "AI Room Matcher",
                    provider: {
                        "@type": "Organization",
                        name: "Art For My Room",
                        url: window.location.origin,
                    },
                    description:
                        "An AI and computer vision service that analyses room images and recommends paintings that match colour, style and atmosphere.",
                    areaServed: "UK",
                    serviceType: "Computer Vision, Image Matching, Visual Search, Room Decor Matching",
                },
                {
                    "@type": "FAQPage",
                    mainEntity: [
                        {
                            "@type": "Question",
                            name: "What is the AI Room Matcher?",
                            acceptedAnswer: {
                                "@type": "Answer",
                                text: "The AI Room Matcher is a room decor matching tool that analyses a room image and suggests paintings that visually fit the space.",
                            },
                        },
                        {
                            "@type": "Question",
                            name: "How does the room matcher work?",
                            acceptedAnswer: {
                                "@type": "Answer",
                                text: "It uses image embeddings and computer vision to compare the uploaded room image against a library of paintings and ranks the closest matches.",
                            },
                        },
                        {
                            "@type": "Question",
                            name: "Can Art For My Room build image and vision systems using AI?",
                            acceptedAnswer: {
                                "@type": "Answer",
                                text: "Yes. Art For My Room can build AI image matching, computer vision, visual search, classification, recommendation and image workflow systems.",
                            },
                        },
                    ],
                },
            ],
        });
        document.head.appendChild(schema);
    }, []);

    useEffect(() => {
        let ignore = false;

        const loadPaintings = async () => {
            setLoadingPaintings(true);
            try {
                const response = await fetch("/api/paintings/");
                const data = await response.json();

                const rawItems = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];

                const normalised = rawItems.map((item) => ({
                    id: item.id,
                    title: item.title,
                    artist: item.artist,
                    image: extractImageUrl(item),
                    embedding: normaliseEmbedding(item.embedding),
                }));

                if (!ignore) {
                    setPaintings(normalised);
                }
            } catch {
                if (!ignore) {
                    setPaintings([]);
                }
            } finally {
                if (!ignore) {
                    setLoadingPaintings(false);
                }
            }
        };

        loadPaintings();

        return () => {
            ignore = true;
        };
    }, []);

    const featuredPaintings = useMemo(() => {
        const live = paintings
            .filter((item) => item.image)
            .slice(0, 4)
            .map((item) => ({
                title: item.title || "Untitled",
                artist: item.artist || "Unknown artist",
                image: item.image,
            }));

        return live.length > 0 ? live : featuredFallback;
    }, [paintings]);

    const onSelectFile = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setImage(file);
        setError("");
        setMatches([]);

        const nextPreview = URL.createObjectURL(file);
        setPreviewUrl(nextPreview);
    };

    const triggerFileInput = () => {
        document.getElementById("room-matcher-file-input")?.click();
    };

    const findMatches = async () => {
        setError("");

        if (!image) {
            setError("Please upload a room photo first.");
            return;
        }

        if (!paintings.length) {
            setError("The painting library is still loading. Please try again in a moment.");
            return;
        }

        setUploading(true);

        try {
            const formData = new FormData();
            formData.append("image", image);

            const response = await fetch("/api/compute-embedding/", {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                throw new Error("We could not analyse your room image right now.");
            }

            const data = await response.json();
            const roomEmbedding = normaliseEmbedding(data.embedding);

            const scored = paintings
                .map((item) => ({
                    ...item,
                    score: cosineSimilarity(roomEmbedding, item.embedding),
                }))
                .filter((item) => Number.isFinite(item.score) && item.score > 0)
                .sort((a, b) => b.score - a.score)
                .slice(0, 6);

            if (!scored.length) {
                setError("No matching paintings were found from the current library.");
                setMatches([]);
            } else {
                setMatches(scored);
                setTimeout(() => {
                    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 180);
            }
        } catch (err) {
            setError(err.message || "Something went wrong while finding matches.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: BRAND.bg, position: "relative", overflow: "hidden" }}>
            <DreamBackground />
            <GlowLayer />

            <Container
                maxWidth="xl"
                sx={{
                    pt: { xs: 8, md: 10 },
                    pb: { xs: 8, md: 11 },
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <Grid container spacing={7} alignItems="center" ref={heroRef}>
                    <Grid item xs={12} lg={7}>
                        <MotionBox
                            initial={{ opacity: 0, x: -34 }}
                            animate={isInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ duration: 0.75 }}
                        >
                            <Chip
                                icon={<AutoAwesomeIcon />}
                                label="AI Room Matcher • Room decor matching • Painting discovery • Computer vision"
                                sx={{
                                    mb: 2.6,
                                    backgroundColor: alpha(BRAND.turquoise, 0.09),
                                    color: BRAND.turquoiseDark,
                                    fontWeight: 800,
                                }}
                            />

                            <Typography
                                variant="h1"
                                sx={{
                                    color: BRAND.ink,
                                    fontWeight: 900,
                                    lineHeight: 1.04,
                                    letterSpacing: "-0.03em",
                                    mb: 2,
                                    fontSize: { xs: "2.4rem", md: "4.2rem" },
                                }}
                            >
                                Find paintings that
                                <Box component="span" sx={{ color: BRAND.turquoiseDark, display: "block" }}>
                                    fit your room decor.
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    color: BRAND.text,
                                    fontSize: { xs: "1rem", md: "1.1rem" },
                                    lineHeight: 1.9,
                                    maxWidth: 760,
                                    mb: 3,
                                }}
                            >
                                Upload a room photo and let the AI compare mood, colour balance, visual rhythm,
                                and atmosphere against a painting library. This room decor matcher is designed for
                                interior inspiration, painting discovery, visual search, and elegant AI-powered image matching.
                            </Typography>

                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 4 }}>
                                {[
                                    "AI room matcher",
                                    "Interior design AI",
                                    "Painting recommendation engine",
                                    "Visual search",
                                    "Computer vision matching",
                                    "Room style analysis",
                                ].map((item) => (
                                    <Chip
                                        key={item}
                                        label={item}
                                        sx={{
                                            backgroundColor: "#FFFFFF",
                                            border: `1px solid ${alpha(BRAND.turquoise, 0.16)}`,
                                            color: BRAND.ink,
                                            fontWeight: 700,
                                        }}
                                    />
                                ))}
                            </Stack>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap flexWrap="wrap">
                                <HeroButton onClick={() => document.getElementById("room-matcher-tool")?.scrollIntoView({ behavior: "smooth" })}>
                                    Try the room matcher
                                </HeroButton>

                                <SecondaryButton component={RouterLink} to="/creative-tech-opportunities">
                                    Explore our AI services
                                </SecondaryButton>
                            </Stack>
                        </MotionBox>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                        <MotionBox
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={isInView ? { opacity: 1, scale: 1 } : {}}
                            transition={{ duration: 0.8, delay: 0.14 }}
                        >
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <FloatingCard>
                                        <GlassCard sx={{ p: 3.2, textAlign: "center" }}>
                                            <CameraAltOutlinedIcon sx={{ fontSize: 40, color: BRAND.turquoiseDark, mb: 1.5 }} />
                                            <Typography sx={{ color: BRAND.ink, fontWeight: 800, mb: 0.8 }}>
                                                Upload room
                                            </Typography>
                                            <Typography sx={{ color: BRAND.text, lineHeight: 1.75 }}>
                                                Start from a real interior image.
                                            </Typography>
                                        </GlassCard>
                                    </FloatingCard>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FloatingCard sx={{ animationDelay: "0.9s" }}>
                                        <GlassCard sx={{ p: 3.2, textAlign: "center" }}>
                                            <ImageSearchOutlinedIcon sx={{ fontSize: 40, color: BRAND.purple, mb: 1.5 }} />
                                            <Typography sx={{ color: BRAND.ink, fontWeight: 800, mb: 0.8 }}>
                                                AI compares
                                            </Typography>
                                            <Typography sx={{ color: BRAND.text, lineHeight: 1.75 }}>
                                                The image is matched to paintings.
                                            </Typography>
                                        </GlassCard>
                                    </FloatingCard>
                                </Grid>

                                <Grid item xs={12}>
                                    <FloatingCard sx={{ animationDelay: "1.4s" }}>
                                        <GlassCard sx={{ p: 3.4 }}>
                                            <Typography sx={{ color: BRAND.ink, fontWeight: 900, fontSize: "1.2rem", mb: 1.6 }}>
                                                What this page helps with
                                            </Typography>

                                            <Stack spacing={1.3}>
                                                {[
                                                    "Finding art that suits a room’s colour palette",
                                                    "Exploring painting options for home decor and interiors",
                                                    "Testing AI image matching and computer vision for real use",
                                                    "Showing how visual recommendation systems can feel elegant and useful",
                                                ].map((line) => (
                                                    <Stack key={line} direction="row" spacing={1.2} alignItems="flex-start">
                                                        <CheckCircleRoundedIcon sx={{ color: BRAND.turquoiseDark, mt: "2px", fontSize: 19 }} />
                                                        <Typography sx={{ color: BRAND.text, lineHeight: 1.75 }}>
                                                            {line}
                                                        </Typography>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </GlassCard>
                                    </FloatingCard>
                                </Grid>
                            </Grid>
                        </MotionBox>
                    </Grid>
                </Grid>
            </Container>

            <Box sx={{ backgroundColor: BRAND.turquoiseSoft, py: { xs: 7, md: 9 }, position: "relative", zIndex: 1 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={3}>
                        {[
                            {
                                icon: <PaletteOutlinedIcon sx={{ fontSize: 34, color: BRAND.turquoiseDark }} />,
                                title: "Room decor matching",
                                text: "The page is built around colour, mood, composition, and visual fit.",
                            },
                            {
                                icon: <VisibilityOutlinedIcon sx={{ fontSize: 34, color: BRAND.purple }} />,
                                title: "Visual recommendation",
                                text: "It turns image understanding into a clear recommendation experience.",
                            },
                            {
                                icon: <ApartmentOutlinedIcon sx={{ fontSize: 34, color: BRAND.gold }} />,
                                title: "Interior inspiration",
                                text: "Useful for homes, offices, gifts, styling ideas, and artistic discovery.",
                            },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <GlassCard sx={{ p: 4 }}>
                                    <Box sx={{ mb: 2 }}>{item.icon}</Box>
                                    <Typography sx={{ color: BRAND.ink, fontWeight: 900, mb: 1 }}>
                                        {item.title}
                                    </Typography>
                                    <Typography sx={{ color: BRAND.text, lineHeight: 1.8 }}>
                                        {item.text}
                                    </Typography>
                                </GlassCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Container
                id="room-matcher-tool"
                maxWidth="lg"
                sx={{ py: { xs: 8, md: 10 }, position: "relative", zIndex: 1 }}
            >
                <Grid container spacing={5} alignItems="stretch">
                    <Grid item xs={12} md={5}>
                        <GlassCard sx={{ p: 4, height: "100%" }}>
                            <Chip
                                icon={<TravelExploreOutlinedIcon />}
                                label="AI room analysis"
                                sx={{
                                    mb: 2,
                                    backgroundColor: BRAND.turquoiseSoft,
                                    color: BRAND.turquoiseDark,
                                    fontWeight: 800,
                                }}
                            />

                            <Typography sx={{ color: BRAND.ink, fontWeight: 900, fontSize: { xs: "1.7rem", md: "2rem" }, mb: 1.4 }}>
                                Upload your room image
                            </Typography>

                            <Typography sx={{ color: BRAND.text, lineHeight: 1.85, mb: 3 }}>
                                The system analyses your uploaded image and compares it to paintings using AI embeddings.
                                It is a clean example of room image analysis, computer vision, visual search, and recommendation logic.
                            </Typography>

                            <UploadDropzone onClick={triggerFileInput}>
                                <input
                                    id="room-matcher-file-input"
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={onSelectFile}
                                />

                                <CloudUploadIcon sx={{ fontSize: 54, color: BRAND.turquoiseDark, mb: 2 }} />

                                <Typography sx={{ color: BRAND.ink, fontWeight: 800, fontSize: "1.18rem", mb: 0.8 }}>
                                    Click to upload a room photo
                                </Typography>

                                <Typography sx={{ color: BRAND.text }}>
                                    JPG, PNG, or WebP
                                </Typography>

                                {image && (
                                    <Chip
                                        icon={<CheckCircleRoundedIcon />}
                                        label={image.name}
                                        sx={{
                                            mt: 2.2,
                                            backgroundColor: alpha(BRAND.turquoise, 0.12),
                                            color: BRAND.turquoiseDark,
                                            fontWeight: 800,
                                            maxWidth: "100%",
                                        }}
                                    />
                                )}
                            </UploadDropzone>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
                                <HeroButton
                                    onClick={findMatches}
                                    disabled={uploading || loadingPaintings}
                                    fullWidth
                                    endIcon={uploading ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : <ArrowForwardRoundedIcon />}
                                >
                                    {uploading ? "Finding matches..." : "Find matching paintings"}
                                </HeroButton>
                            </Stack>

                            {error && (
                                <Alert severity="error" sx={{ mt: 2.5, borderRadius: 3 }}>
                                    {error}
                                </Alert>
                            )}
                        </GlassCard>
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <GlassCard sx={{ p: 4, height: "100%" }}>
                            <Typography sx={{ color: BRAND.ink, fontWeight: 900, fontSize: { xs: "1.5rem", md: "1.9rem" }, mb: 1.5 }}>
                                Preview and room mood
                            </Typography>

                            <Typography sx={{ color: BRAND.text, lineHeight: 1.85, mb: 3 }}>
                                This section gives the user a calm upload experience before the visual matching begins.
                            </Typography>

                            {previewUrl ? (
                                <Box
                                    sx={{
                                        borderRadius: 4,
                                        overflow: "hidden",
                                        border: `1px solid ${BRAND.border}`,
                                        backgroundColor: BRAND.subtle,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={previewUrl}
                                        alt="Uploaded room preview"
                                        sx={{
                                            width: "100%",
                                            height: { xs: 280, md: 460 },
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        minHeight: { xs: 280, md: 460 },
                                        borderRadius: 4,
                                        border: `1px solid ${BRAND.border}`,
                                        backgroundColor: BRAND.subtle,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        p: 4,
                                        textAlign: "center",
                                    }}
                                >
                                    <Box>
                                        <CameraAltOutlinedIcon sx={{ fontSize: 56, color: BRAND.turquoiseDark, mb: 2 }} />
                                        <Typography sx={{ color: BRAND.ink, fontWeight: 800, mb: 1 }}>
                                            Your uploaded room will appear here
                                        </Typography>
                                        <Typography sx={{ color: BRAND.text, maxWidth: 420 }}>
                                            Once uploaded, the room preview becomes the starting point for painting discovery and room decor matching.
                                        </Typography>
                                    </Box>
                                </Paper>
                            )}
                        </GlassCard>
                    </Grid>
                </Grid>
            </Container>

            <Container
                ref={resultsRef}
                maxWidth="lg"
                sx={{ pb: { xs: 8, md: 10 }, position: "relative", zIndex: 1 }}
            >
                <Box sx={{ mb: 3 }}>
                    <Typography sx={{ color: BRAND.ink, fontWeight: 900, fontSize: { xs: "1.9rem", md: "2.5rem" }, mb: 1 }}>
                        Best matches
                    </Typography>
                    <Typography sx={{ color: BRAND.text, lineHeight: 1.8 }}>
                        The AI ranks paintings by visual fit. This gives visitors a practical room-to-art matching experience rather than a vague gallery browse.
                    </Typography>
                </Box>

                {loadingPaintings ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            border: `1px solid ${BRAND.border}`,
                            textAlign: "center",
                        }}
                    >
                        <CircularProgress />
                        <Typography sx={{ mt: 2, color: BRAND.text }}>
                            Loading painting library...
                        </Typography>
                    </Paper>
                ) : matches.length > 0 ? (
                    <Grid container spacing={3}>
                        {matches.map((item, index) => (
                            <MatchCard key={`${item.id || item.title}-${index}`} item={item} index={index} />
                        ))}
                    </Grid>
                ) : (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            border: `1px solid ${BRAND.border}`,
                            textAlign: "center",
                        }}
                    >
                        <ImageSearchOutlinedIcon sx={{ fontSize: 46, color: BRAND.turquoiseDark, mb: 1 }} />
                        <Typography sx={{ color: BRAND.ink, fontWeight: 800, mb: 1 }}>
                            Upload a room to see your matches
                        </Typography>
                        <Typography sx={{ color: BRAND.text }}>
                            The top paintings will appear here after the system compares your room image against the painting library.
                        </Typography>
                    </Paper>
                )}
            </Container>

            <Box sx={{ backgroundColor: BRAND.subtle, py: { xs: 8, md: 10 }, position: "relative", zIndex: 1 }}>
                <Container maxWidth="lg">
                    <Typography
                        sx={{
                            color: BRAND.ink,
                            fontWeight: 900,
                            fontSize: { xs: "1.9rem", md: "2.5rem" },
                            textAlign: "center",
                            mb: 1.5,
                        }}
                    >
                        Featured paintings
                    </Typography>

                    <Typography
                        sx={{
                            color: BRAND.text,
                            textAlign: "center",
                            lineHeight: 1.8,
                            maxWidth: 760,
                            mx: "auto",
                            mb: 5,
                        }}
                    >
                        A small look into the kind of visual language the room matcher can connect to your space.
                    </Typography>

                    <Grid container spacing={3}>
                        {featuredPaintings.map((painting, index) => (
                            <Grid item xs={12} sm={6} lg={3} key={`${painting.title}-${index}`}>
                                <GlassCard>
                                    <CardMedia
                                        component="img"
                                        image={painting.image}
                                        alt={painting.title}
                                        sx={{ height: 260, objectFit: "cover" }}
                                    />
                                    <CardContent sx={{ p: 2.6 }}>
                                        <Typography sx={{ color: BRAND.ink, fontWeight: 800, mb: 0.5 }}>
                                            {painting.title}
                                        </Typography>
                                        <Typography sx={{ color: BRAND.text }}>
                                            {painting.artist}
                                        </Typography>
                                    </CardContent>
                                </GlassCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 }, position: "relative", zIndex: 1 }}>
                <Typography
                    sx={{
                        color: BRAND.ink,
                        fontWeight: 900,
                        fontSize: { xs: "1.9rem", md: "2.5rem" },
                        textAlign: "center",
                        mb: 1.4,
                    }}
                >
                    Buyer testimonials
                </Typography>

                <Typography
                    sx={{
                        color: BRAND.text,
                        textAlign: "center",
                        lineHeight: 1.8,
                        maxWidth: 760,
                        mx: "auto",
                        mb: 5,
                    }}
                >
                    Replace the cards below with your real buyer quotes for stronger trust, SEO depth, and conversion.
                </Typography>

                <Grid container spacing={3}>
                    {buyerTestimonials.map((item) => (
                        <Grid item xs={12} md={4} key={item.quote}>
                            <GlassCard sx={{ p: 3.4 }}>
                                <Typography sx={{ color: BRAND.text, lineHeight: 1.85, fontStyle: "italic", mb: 2.4 }}>
                                    “{item.quote}”
                                </Typography>

                                <Divider sx={{ mb: 2 }} />

                                <Typography sx={{ color: BRAND.ink, fontWeight: 800 }}>
                                    {item.name}
                                </Typography>
                                <Typography sx={{ color: BRAND.turquoiseDark, mt: 0.4 }}>
                                    {item.role}
                                </Typography>
                            </GlassCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            <Box
                sx={{
                    py: { xs: 8, md: 10 },
                    background: `linear-gradient(180deg, #FFFFFF 0%, ${alpha(BRAND.turquoise, 0.08)} 100%)`,
                    borderTop: `1px solid ${alpha(BRAND.turquoise, 0.12)}`,
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={5} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Chip
                                icon={<InsightsOutlinedIcon />}
                                label="AI image and vision systems"
                                sx={{
                                    mb: 2,
                                    backgroundColor: BRAND.turquoiseSoft,
                                    color: BRAND.turquoiseDark,
                                    fontWeight: 800,
                                }}
                            />

                            <Typography sx={{ color: BRAND.ink, fontWeight: 900, fontSize: { xs: "2rem", md: "2.8rem" }, mb: 2 }}>
                                We can build image and vision systems using AI.
                            </Typography>

                            <Typography sx={{ color: BRAND.text, lineHeight: 1.9, fontSize: "1.05rem", mb: 3 }}>
                                This room matcher is one example. We can also build AI image matching systems,
                                computer vision workflows, visual search tools, classification systems, recommendation engines,
                                room decor intelligence, aesthetic analysis, and image-led discovery products for businesses and organisations.
                            </Typography>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap flexWrap="wrap">
                                <HeroButton component={RouterLink} to="/creative-tech-opportunities" endIcon={<ArrowForwardRoundedIcon />}>
                                    Explore our AI services
                                </HeroButton>

                                <SecondaryButton component="a" href="mailto:tartalizing@gmail.com">
                                    Enquire by email
                                </SecondaryButton>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <GlassCard sx={{ p: 3.4 }}>
                                <Stack spacing={1.6}>
                                    {[
                                        "Computer vision interfaces",
                                        "Visual search and image recommendation",
                                        "Image classification and content analysis",
                                        "Aesthetic and decor matching systems",
                                        "Image workflow automation",
                                    ].map((line) => (
                                        <Stack key={line} direction="row" spacing={1.2} alignItems="flex-start">
                                            <CheckCircleRoundedIcon sx={{ color: BRAND.turquoiseDark, mt: "2px", fontSize: 19 }} />
                                            <Typography sx={{ color: BRAND.text, lineHeight: 1.75 }}>
                                                {line}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </GlassCard>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
}
